import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  updateCourseContent,
  createCourseContent,
} from "../../../../service/AdminService/BookService";
import CustomModal from "../../../../components/global/CustomModal/CustomModal";
import Form from "../../../../components/shared/new Form/Fields/Form";
import { useFormValidation } from "../../../../utility/validationHooks/useFormValidation";
import { showSuccessMessage, showErrorMessage } from "../../../../components/shared/pagination/messages";
import { getCourseContentFields, COURSE_CONTENT_SECTION_CONFIG } from "../CourseContentFields";
import { courseContentValidationSchema } from "../CourseContentValidationSchema";

const CourseContentModal = ({
  isModalOpen,
  handleCloseModal,
  selectedContent,
  setLoading,
  loadContent,
  courseId,
  courseTitle,
}) => {
  const formDataRef = useRef({});
  const isEditing = Boolean(selectedContent);
  const [formData, setFormData] = useState({});

  const validationSchema = useMemo(() => courseContentValidationSchema(isEditing), [isEditing]);
  const { errors, validateField, validateForm, touchField, setErrors } = useFormValidation(validationSchema);

  const getFileName = (url) => {
    if (!url) return "existing-file";
    const raw = url.split("/").pop().split("?")[0];
    const parts = raw.split("_");
    parts.shift();
    return parts.join("_");
  };

  const normalizeContentType = (contentType) => {
    if (!contentType) return "";
    return contentType.toUpperCase().replace(/-/g, "_");
  };

  const getResourceField = (contentType, resourceLink) => {
    if (contentType === "YOUTUBE_LINK") return { resourceUrl: resourceLink };
    if (contentType === "VIDEO") return { file: [{ uid: "-1", name: getFileName(resourceLink), status: "done", url: resourceLink, resourceUrl: resourceLink }] };
    if (contentType === "PDF") return { pdfdocument: [{ uid: "-1", name: getFileName(resourceLink), status: "done", url: resourceLink, resourceUrl: resourceLink }] };
    return {};
  };

  useEffect(() => {
    if (isModalOpen) {
      setFormData(
        isEditing
          ? {
            title: selectedContent?.title ?? "",
            description: selectedContent?.description ?? "",
            isActive: selectedContent?.active ? "true" : "false",
            contentType: normalizeContentType(selectedContent?.contentType),
            minCompletionPercentage: selectedContent?.minCompletionPercentage ?? 0,
            ...getResourceField(normalizeContentType(selectedContent?.contentType), selectedContent?.resourceLink),
          }
          : {
            isActive: "",
            minCompletionPercentage: "",
            contentType: "",
          }
      );
    } else {
      setFormData({});
      setErrors({});
    }
  }, [isModalOpen, isEditing, selectedContent, setErrors]);

  const handleFormChange = useCallback((name, value) => {
    let finalValue = value;

    if (name === "file" || name === "pdfdocument") {
      finalValue = Array.isArray(value) ? value : (value?.fileList ?? []);
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: finalValue };
      formDataRef.current = updated;
      return updated;
    });
  }, []);

  const handleBlur = useCallback((name) => {
    touchField(name);
    validateField(name, formDataRef.current[name], formDataRef.current);
  }, [formData, touchField, validateField]);

  const handleSubmit = useCallback(async () => {
    const { isValid } = validateForm(formData);

    if (!isValid) {
      showErrorMessage("Please fill all the required field.");
      return;
    }

    const isFileType = ["VIDEO", "PDF"].includes(formData.contentType);
    const isYouTube = formData.contentType === "YOUTUBE_LINK";

    const fileArray = formData.contentType === "VIDEO" ? formData.file : formData.pdfdocument;
    const uploadedFile = (Array.isArray(fileArray) && fileArray.length > 0)
      ? fileArray[fileArray.length - 1].originFileObj
      : null;

    const hasNewFile = uploadedFile instanceof File;

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("courseId", courseId);

      if (isEditing) {
        const fieldMap = {
          title: "title",
          description: "description",
          isActive: "active",     
          contentType: "contentType",
          minCompletionPercentage: "minCompletionPercentage",
        };

        const changedFields = Object.keys(fieldMap).reduce((acc, key) => {
          let originalValue = selectedContent?.[fieldMap[key]] ?? "";

          if (key === "isActive") originalValue = originalValue ? "true" : "false";
          if (key === "contentType") originalValue = normalizeContentType(originalValue);

          if (String(formData[key]) !== String(originalValue)) {
            acc[key] = formData[key];
          }
          return acc;
        }, {});

        if ("title" in changedFields) payload.append("title", changedFields.title);
        if ("description" in changedFields) payload.append("description", changedFields.description);
        if ("isActive" in changedFields) payload.append("isActive", changedFields.isActive === "true");
        if ("contentType" in changedFields) payload.append("contentType", changedFields.contentType);
        if ("minCompletionPercentage" in changedFields) payload.append("minCompletionPercentage", Number(changedFields.minCompletionPercentage));

        if (isFileType) {
          if (hasNewFile) {
            payload.append("file", uploadedFile);
          } else if ("contentType" in changedFields) {
            payload.append("resourceUrl", selectedContent?.resourceLink ?? "");
          }
        } else if (isYouTube) {
          const originalUrl = selectedContent?.resourceLink ?? "";
          if (formData.resourceUrl !== originalUrl) {
            payload.append("resourceUrl", formData.resourceUrl);
          }
        }

        await updateCourseContent(selectedContent.courseContentId, payload);
        showSuccessMessage("Content updated successfully!");

      } else {
        payload.append("title", formData.title);
        payload.append("description", formData.description);
        payload.append("isActive", formData.isActive === "true");
        payload.append("contentType", formData.contentType);
        payload.append("minCompletionPercentage", Number(formData.minCompletionPercentage));

        if (isFileType && hasNewFile) payload.append("file", uploadedFile);
        else if (isYouTube) payload.append("resourceUrl", formData.resourceUrl ?? "");

        await createCourseContent(payload);
        showSuccessMessage("Content added successfully!");
      }

      loadContent();
      handleCloseModal();
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to save content";
      showErrorMessage(message);
    } finally {
      setLoading(false);
    }
  }, [formData, isEditing, selectedContent, courseId, validateForm, loadContent, handleCloseModal]);

  const fields = useMemo(() => getCourseContentFields(formData.contentType), [formData.contentType]);

  return (
    <CustomModal
      visible={isModalOpen}
      onCancel={handleCloseModal}
      title={`${isEditing ? "Edit" : "Add"} Content for "${courseTitle}"`}

    >
      <div>
        <Form
          fields={fields}
          formData={formData}
          sectionConfig={COURSE_CONTENT_SECTION_CONFIG}
          onChange={handleFormChange}
          onBlur={handleBlur}
          onSubmit={handleSubmit}
          errors={errors}
          setErrors={setErrors}
          showCancelButton={true}
          showClearButton={false}
          onCancel={handleCloseModal}
        />
      </div>
    </CustomModal>
  );
};

export default CourseContentModal;